import {parseReference} from '@apps-common/geocorn-sdk/api/util.js';

// Thin wrapper over the GeocornSDK reference parser: adds the null / short-ref
// guards our consumers rely on and keeps our `itemId` naming (the SDK calls it
// `featureId`). Reusing the SDK keeps a single source of the pl:ref grammar.
export function parseFeatureRef(ref) {
  if (!ref || typeof ref !== 'string') return null;
  // pl:features/{dataset}/{collectionId}/{itemId?}
  // AZ refs are 3-part (collection); reservation aoi_refs are 4-part (a specific
  // feature within a collection). itemId is null for the 3-part form.
  if (ref.split('/').length < 3) return null;
  const {dataset, collectionId, featureId} = parseReference(ref);
  return {dataset, collectionId, itemId: featureId ?? null};
}

// The bare feature hash within a collection. A reservation's aoi_ref itemId may
// be a slugid (e.g. `field-name-Xy7Kp`) when the ref was built with slugs, but
// Granero's MVT tiles emit only the trailing hash (`Xy7Kp`) in each feature's
// `pl:ref`. Take the trailing `-` token so the two match client-side (mirrors
// granero's own pk_from_slugid = rsplit("-", 1)[-1]).
export function featureHashid(itemId) {
  if (!itemId || typeof itemId !== 'string') return null;
  return itemId.split('-').pop() || null;
}

// Group reservation rows by their collection for map rendering. Only `reserved`
// rows count as live reservations (cancelled/pending are excluded). aoi_ref is
// unique per product access (DB constraint), so no extra dedup is needed, but we
// guard with a Set anyway. Returns one entry per collection with the set of
// reserved feature hashes (for client-side tile coloring) and a count.
export function groupReservationsByCollection(rows) {
  const groups = new Map();
  for (const row of rows ?? []) {
    if (!row || row.state !== 'reserved') continue;
    const parsed = parseFeatureRef(row.aoi_ref);
    if (!parsed || !parsed.itemId) continue;
    const {dataset, collectionId, itemId} = parsed;
    const hashid = featureHashid(itemId);
    if (!hashid) continue;
    if (!groups.has(collectionId)) {
      groups.set(collectionId, {
        dataset,
        collectionId,
        itemIds: new Set(),
      });
    }
    groups.get(collectionId).itemIds.add(hashid);
  }
  return Array.from(groups.values()).map(g => ({
    dataset: g.dataset,
    collectionId: g.collectionId,
    itemIds: g.itemIds,
    count: g.itemIds.size,
  }));
}

export function parseAccessibilityZoneRefs(refs) {
  if (!Array.isArray(refs) || !refs.length) return [];
  const seen = new Set();
  const results = [];
  for (const ref of refs) {
    const parsed = parseFeatureRef(ref);
    if (parsed && !seen.has(parsed.collectionId)) {
      seen.add(parsed.collectionId);
      // AZ consumers only care about the collection; keep the historical
      // {dataset, collectionId} shape (drop the reservation-only itemId).
      results.push({
        dataset: parsed.dataset,
        collectionId: parsed.collectionId,
      });
    }
  }
  return results;
}

function isValidBbox(bbox) {
  return (
    Array.isArray(bbox) &&
    bbox.length >= 4 &&
    bbox.slice(0, 4).every(Number.isFinite)
  );
}

export function unionExtents(bboxes) {
  const valid = bboxes.filter(isValidBbox);
  if (!valid.length) return null;
  let [minX, minY, maxX, maxY] = valid[0];
  for (let i = 1; i < valid.length; i++) {
    const [x1, y1, x2, y2] = valid[i];
    if (x1 < minX) minX = x1;
    if (y1 < minY) minY = y1;
    if (x2 > maxX) maxX = x2;
    if (y2 > maxY) maxY = y2;
  }
  return [minX, minY, maxX, maxY];
}
